import { Console } from 'console';

export class MultiCTA {
  private checkAvailabilityButton: HTMLButtonElement;
  private contactUsButton: HTMLButtonElement;
  private bookAppointmentButton: HTMLButtonElement;
  private iframeElement: HTMLIFrameElement | null = null;

  private widgetData: {
    containerSelector: string;
    type: string;
    module: string;
    version: string;
  };

  constructor(widgetData: { containerSelector: string; type: string; module: string; version: string }) {
    this.widgetData = widgetData;

    // Create buttons explicitly
    this.checkAvailabilityButton = this.createButton('Check Availability', 'check-availability');
    this.contactUsButton = this.createButton('Contact Us', 'contact-us');
    this.bookAppointmentButton = this.createButton('Book an Appointment', 'book-appointment');

    // Initialize iframe connection
    // this.handleConnectIframe();

    this.handleConnectIframe('multi-cta');
    // this.handleConnectIframe('contact-us');
    // this.handleConnectIframe('book-appointment');

    // Setup event listeners
    this.setupEventListeners();
  }

  render(parentElement: HTMLElement): void {
    if (!document.getElementById(this.checkAvailabilityButton.id)) {
      parentElement.appendChild(this.checkAvailabilityButton);
    }
    if (!document.getElementById(this.contactUsButton.id)) {
      parentElement.appendChild(this.contactUsButton);
    }
    if (!document.getElementById(this.bookAppointmentButton.id)) {
      parentElement.appendChild(this.bookAppointmentButton);
    }
  }

  private createButton(text: string, module: string): HTMLButtonElement {
    const button = document.createElement('button');
    button.id = `open-iframe-button-${module}`;
    button.textContent = text;

    // Apply button styles
    button.style.cssText = `
      background-color: #5e50f1;
      color: white;
      border: 1px solid #5e50f1;
      border-radius: 4px;
      padding: 11px 14px;
      cursor: pointer;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      font-weight: 500;
      outline: none;
      visibility: hidden;
      width: 100%;
      margin: 5px;
    `;

    // Add event listener to open the respective iframe
    button.addEventListener('click', () => this.handleOpenIframe(module));

    return button;
  }

  private handleOpenIframe(module: string): void {
    this.removeExistingIframe();

    this.iframeElement = this.createIframe(module);
    this.iframeElement.style.cssText = `
      width: 100vw;
      height: 100vh;
      border: none;
      position: fixed;
      top: 0;
      left: 0;
      z-index: 1000000;
      display: block;
    `;
    document.body.appendChild(this.iframeElement);
    document.body.style.overflow = 'hidden';

    window.postMessage({ eventType: 'widgetOpened', data: { module } }, '*');
  }

  private handleConnectIframe(module: string): void {
    this.removeExistingIframe();
    this.iframeElement = this.createIframe(module);
    this.iframeElement.style.visibility = 'hidden';
    document.body.appendChild(this.iframeElement);
  }

  private handleIframeClose(): void {
    this.removeExistingIframe();
    document.body.style.overflow = '';
    window.postMessage({ eventType: 'widgetClosed' }, '*');
  }

  // Function to find 18-character alphanumeric strings in webpage text and URL
  private findAlphanumericInPageAndURL(): void {
    const regex = /\b[A-Za-z0-9]{17}\b/g; // Regular expression for 18-character alphanumeric strings

    // 1. Search in the entire text content of the webpage
    const bodyText = document.body.innerText; // Get all visible text on the page
    const textMatches = bodyText.match(regex) || [];

    // 2. Search in the current webpage URL
    const url = window.location.href; // Get the current webpage URL
    const urlMatches = url.match(regex) || [];

    // Combine results and remove duplicates
    const allMatches = [...new Set([...textMatches, ...urlMatches])];

    // Return the results as an object
    return {
      textMatches,
      urlMatches,
      allMatches,
    };
  }

  private createIframe(module: string): HTMLIFrameElement {
    const result = this.findAlphanumericInPageAndURL();

    const iframe = document.createElement('iframe');

    const firstMatch = result?.allMatches[0] || '';

    iframe.id = `${module}-iframe`;

    // iframe.src = `http://localhost:5173/${module}/${this.widgetData.token}?domain=${window.location.origin}&type=${this.widgetData.type}&version=${this.widgetData.version}&VIN=${firstMatch}`;
    iframe.src = `https://uat-widget.apps.dabadu.ai/${module}/${this.widgetData.token}?domain=${window.location.origin}&type=${this.widgetData.type}&version=${this.widgetData.version}&VIN=${firstMatch}`;

    iframe.className = 'iframe-class';
    return iframe;
  }

  private removeExistingIframe(): void {
    if (this.iframeElement) {
      this.iframeElement.remove();
      this.iframeElement = null;
    }
  }

  private setupEventListeners(): void {
    window.addEventListener('message', (event) => {
      if (!event.data || !event.data.res) return;

      const { type, res } = event.data;

      console.log('Received message:', type, res.module);
      // if (!['multi-cta'].includes(res.module)) return;

      const openEventType = `${res.module}-modal-open-btn`;

      if (type === openEventType) {
        this.handleIframeOpen(event.data);
      } else if (type === `${res.module}-modal-close`) {
        this.handleIframeClose();
      }
    });
  }

  private handleIframeOpen(response: any): void {
    const { type, res } = response;

    if (res.success && type.endsWith('-modal-open-btn')) {
      if (res?.widgetData?.checkAvailability?.toUpperCase() === 'TRUE') {
        this.updateButtonVisibility(this.checkAvailabilityButton, res?.checkAvailabilityBtnName, res);
      }
      if (res?.widgetData?.contactUs?.toUpperCase() === 'TRUE') {
        this.updateButtonVisibility(this.contactUsButton, res?.contactUsBtnName, res);
      }
      if (res?.widgetData?.bookAppointment?.toUpperCase() === 'TRUE') {
        this.updateButtonVisibility(this.bookAppointmentButton, res?.bookAppointmentBtnName, res);
      }
    } else {
      this.removeButtons(res.widgetData);
      this.removeExistingIframe();
    }
  }

  private updateButtonVisibility(button: HTMLButtonElement, btnName: string, res: any): void {
    button.style.visibility = 'visible';
    button.style.backgroundColor = res?.primaryColor || res?.bgColor || '#5e50f1';
    button.textContent = btnName || button.textContent;
  }

  private removeButtons(widgetData: any): void {
    if (widgetData?.checkAvailability?.toUpperCase() !== 'TRUE') {
      this.checkAvailabilityButton.remove();
    }
    if (widgetData?.contactUs?.toUpperCase() !== 'TRUE') {
      this.contactUsButton.remove();
    }
    if (widgetData?.bookAppointment?.toUpperCase() !== 'TRUE') {
      this.bookAppointmentButton.remove();
    }
  }
}
